import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { ILegalEntityLookup } from 'APIPackage/FnsModule/interfaces/ILegalEntityLookup';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { ITargetEntity } from 'APIPackage/DictionariesModule/interfaces/ITargetEntity';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { IKnmDistrictSelectOption } from 'CorePackage/CatalogsModule/interfaces/IKnmDistrictSelectOption';
import { findRegionSelectOption } from 'KnmPackage/MainModule/components/regions';
import { mspCodesByMspCategoryDigitCodes } from 'KnmPackage/MainModule/constants/mspCodesByMspCategoryDigitCodes';
import { MspCategoryDigitCodesEnum } from 'APIPackage/KnmModule/enums/MspCategoryDigitCodesEnum';
import { checkOgrnBySubjectType } from 'CorePackage/MainModule/validators/isOgrn';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { IKnmErknmFormObject } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormObject';

import { KnmSubjectTypesEnum } from '../components/ErknmSubjectInfo';
import { IErknmSubjectFieldPaths } from './useErknmSubjectFields';

// eslint-disable-next-line @typescript-eslint/naming-convention
interface UseErknmSubjectHandlersProps {
  formRef: IKnmFormRef | undefined;
  name: string | undefined;
  subjectType: IDictionarySelectOption<ITargetEntity> | undefined;
  isUR: boolean;
  isIP: boolean;
  isExtended: boolean;
  districtSelectOptions: IKnmDistrictSelectOption[];
  setCheckByInnMessage?: (message: string | undefined) => void;
  isKnmStatusFillInProcess: boolean;
  isChangeSubjectTypeFromActionsAvailable?: boolean;
  setRafpNzaNotFoundText: (msg: string | undefined) => void;
  subjectFieldNames: IErknmSubjectFieldPaths;
}

export const useErknmSubjectHandlers = ({
  formRef,
  name,
  subjectType,
  isUR,
  isIP,
  isExtended,
  districtSelectOptions,
  setCheckByInnMessage,
  isKnmStatusFillInProcess,
  isChangeSubjectTypeFromActionsAvailable,
  setRafpNzaNotFoundText,
  subjectFieldNames,
}: UseErknmSubjectHandlersProps) => {
  const dispatch = useDispatch();

  const clearItAccreditation = useCallback(() => {
    const change = (field: string) => formRef?.current.change(field as keyof IKnmForm, undefined);

    change(subjectFieldNames.isPresentInItRegistry);
    change(subjectFieldNames.itAccreditationResponse);
  }, [formRef]);

  const handleLegalEntityAutoCompleteSelect = useCallback(
    async (legalEntityLookup: ILegalEntityLookup) => {
      if (subjectType?.label === KnmSubjectTypesEnum.IUR) return;

      const {
        ogrn: legalEntityOgrn,
        inn: legalEntityInn,
        orgName: legalEntityOrgName,
        dateReg: legalEntityDateReg,
        status: legalEntityStatus,
        leAddress: legalEntityLeAddress,
        workAddress: legalEntityWorkAddress,
        mspCategory: legalMspCategory,
        mspRegistryDate: legalMspRegistryDate,
        leAddressRegionCode,
        leAddressIsFromFias,
      } = legalEntityLookup;

      const change = <F>(field: string, value: F) =>
        formRef?.current.change(field as keyof IKnmForm, value as IKnmForm);

      change(subjectFieldNames.organizationAutocomplete, true);

      if (legalEntityOgrn) change(subjectFieldNames.ogrn, legalEntityOgrn);
      if (legalEntityInn) change(subjectFieldNames.inn, legalEntityInn);

      if (legalEntityInn && legalEntityOgrn && checkOgrnBySubjectType(subjectType?.label, legalEntityOgrn)) {
        change(subjectFieldNames.isAutocomplete, true);
      }

      if (legalEntityOrgName) change(subjectFieldNames.organizationName, legalEntityOrgName);

      if (legalEntityWorkAddress && isUR) {
        change(subjectFieldNames.workAddress, legalEntityWorkAddress);
      }

      if (legalEntityLeAddress) {
        if (isUR || subjectType?.label === KnmSubjectTypesEnum.FIUR) {
          change(subjectFieldNames.locationAddress, legalEntityLeAddress);
          const isInFias = leAddressIsFromFias === 'true';

          change(subjectFieldNames.locationnotInFIAS, !isInFias);
          change(subjectFieldNames.locationRegionCodeFromFns, leAddressRegionCode);

          const regionOption = await findRegionSelectOption(
            legalEntityLeAddress,
            leAddressRegionCode || undefined,
            districtSelectOptions,
          );

          const finalLocationRegionField = isInFias
            ? subjectFieldNames.locationfiasRegion
            : subjectFieldNames.locationregion;

          change(finalLocationRegionField, regionOption);
        } else if (isIP) {
          change(subjectFieldNames.factualAddress, legalEntityLeAddress);
        }
      }

      if (legalEntityDateReg) {
        change(
          subjectFieldNames.registrationDate,
          formatDate({ date: legalEntityDateReg, dateFormat: DATE_FORMAT_LONG_FNS }),
        );
      }

      if (legalEntityStatus) {
        change(subjectFieldNames.organizationStatus, legalEntityStatus);
      }

      if (legalMspCategory) {
        const mspCode = mspCodesByMspCategoryDigitCodes[Number(legalMspCategory) as MspCategoryDigitCodesEnum];

        change(subjectFieldNames.mspCode, mspCode);
      }
      if (legalMspRegistryDate) {
        change(subjectFieldNames.mspRegistryDate, legalMspRegistryDate);
      }
    },
    [formRef, name, subjectType, isUR, isIP, districtSelectOptions],
  );

  const handleAutoCompleteClear = useCallback(() => {
    const change = (field: string) => formRef?.current.change(field as keyof IKnmForm, undefined);

    change(subjectFieldNames.ogrn);
    change(subjectFieldNames.inn);
    change(subjectFieldNames.organizationName);
    change(subjectFieldNames.workAddress);
    change(subjectFieldNames.locationAddress);
    change(subjectFieldNames.isAutocomplete);
    change(subjectFieldNames.mspCode);

    dispatch({ type: 'KNM_SET_LEGAL_ENTITY_DATA', payload: undefined });

    clearItAccreditation();

    if (isExtended && formRef?.current) {
      const objects = formRef.current.getFieldState('objectsErknm')?.value || [];
      const autocompleteIndex = objects.findIndex((obj: IKnmErknmFormObject) => obj.autocomplete);

      if (autocompleteIndex >= 0) {
        formRef.current.change(`objectsErknm[${autocompleteIndex}].autocomplete` as keyof IKnmForm, false);
      }
    }
  }, [formRef, name, isExtended, dispatch]);

  const handleSubjectTypeChange = useCallback(
    (v: IDictionarySelectOption<ITargetEntity> | null) => {
      const remove = formRef?.current.mutators.remove;
      const change = <F>(field: string, value: F) =>
        formRef?.current.change(field as keyof IKnmForm, value as IKnmForm);

      const clearObjectsNotFromErvk = () => {
        if (!formRef || !remove) return;
        const objectsFieldName: keyof IKnmForm = 'objects';
        const objectsErknmFieldName: keyof IKnmForm = 'objectsErknm';
        const currentValues = formRef.current.getState().values;
        const objectsErknmArray = currentValues[objectsErknmFieldName] || [];
        const objectsArray = currentValues[objectsFieldName] || [];
        const indicesToRemove: number[] = objectsErknmArray.flatMap((obj, index) => (!obj.ervkId ? [index] : []));

        objectsArray.forEach((_, index) => remove(objectsFieldName, index));
        indicesToRemove.forEach(index => remove(objectsErknmFieldName, index));
      };

      change(subjectFieldNames.isFiz, v?.label === KnmSubjectTypesEnum.FIZ);
      change(subjectFieldNames.inn, undefined);
      change(subjectFieldNames.ogrn, undefined);
      change(subjectFieldNames.rafpNza, undefined);
      change(subjectFieldNames.kpp, undefined);
      change(subjectFieldNames.organizationName, undefined);
      change(subjectFieldNames.workAddress, undefined);
      change(subjectFieldNames.locationAddress, undefined);
      change(subjectFieldNames.factualAddress, undefined);
      change(subjectFieldNames.snils, undefined);
      change(subjectFieldNames.document, undefined);
      change(subjectFieldNames.isAutocomplete, false);
      change(subjectFieldNames.isForeignSubjectAgencyNotRegistredInRf, false);
      change(subjectFieldNames.country, undefined);
      change(subjectFieldNames.okveds, undefined);
      change(subjectFieldNames.mspCode, undefined);
      change(subjectFieldNames.smevRequestStatus, undefined);
      change(subjectFieldNames.locationnotInFIAS, false);
      change(subjectFieldNames.location_manualAddress, undefined);
      change(subjectFieldNames.locationregion, undefined);

      clearItAccreditation();
      if (!isKnmStatusFillInProcess && isChangeSubjectTypeFromActionsAvailable) {
        clearObjectsNotFromErvk();
      }
      setCheckByInnMessage?.(undefined);
    },
    [
      formRef,
      name,
      clearItAccreditation,
      setCheckByInnMessage,
      isKnmStatusFillInProcess,
      isChangeSubjectTypeFromActionsAvailable,
    ],
  );

  const handleInnOrKppChange = useCallback(
    (fieldName: 'inn' | 'kpp', value: string | undefined) => {
      const change = <F>(field: string, _value: F) =>
        formRef?.current.change(field as keyof IKnmForm, _value as IKnmForm);

      if (value) {
        change(subjectFieldNames.rafpNza, undefined);
        setRafpNzaNotFoundText(undefined);
      }

      change(fieldName, value);
    },
    [formRef, name, setRafpNzaNotFoundText],
  );

  const handleRafpNzaChange = useCallback(
    (value: string | undefined) => {
      const change = <F>(field: string, _value: F) =>
        formRef?.current.change(field as keyof IKnmForm, _value as IKnmForm);

      if (value) {
        change(subjectFieldNames.inn, undefined);
        change(subjectFieldNames.kpp, undefined);
        change(subjectFieldNames.ogrn, undefined);
        setRafpNzaNotFoundText(undefined);
      }

      change(subjectFieldNames.rafpNza, value);
    },
    [formRef, name, setRafpNzaNotFoundText],
  );

  return {
    handleLegalEntityAutoCompleteSelect,
    handleAutoCompleteClear,
    clearItAccreditation,
    handleSubjectTypeChange,
    handleInnOrKppChange,
    handleRafpNzaChange,
  };
};
